/**
 * @file CanUploadCheck.js
 * @module src/js/components/CanUploadCheck
 * @description
 * **A reusable component to check if upload conditions are met.**
 * - Validates date, title, files, link, and type fields based on provided props.
 * - Displays checkmarks and field names indicating which conditions are met.
 * 
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useMemo } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import { Check, X } from "lucide-react";

export default function CanUploadCheck({
    year = null,
    month = null,
    day = null,
    hasDateField = false, // There are cases where date validation is not needed, but date fields should exist
    title = null,
    files = null,
    isAcceptListEmpty = true,
    link = null,
    type = null,
    setCanUpload,
    editMode = false
}) {
    const { theme } = useTheme();

    // Date is not required if no day field is in the parent upload form
    const isValidDate = useCallback(() => {
        if (year === null || month === null || day === null) return true;

        const date = new Date(year, month - 1, day);
        return String(date.getFullYear()) === year &&
            String(date.getMonth() + 1).padStart(2, "0") === month &&
            String(date.getDate()).padStart(2, "0") === day;
    }, [year, month, day]);

    // Remove falsy values from conditions array
    const conditions = useMemo(() => [
        ...(hasDateField ? [isValidDate()] : []),
        ...(title !== null ? [title !== ''] : []),
        ...(link !== null ? [link !== '' && (link.startsWith('http://') || link.startsWith('https://'))] : []),
        ...(type !== null ? [type !== 'choose'] : []),
        ...(!isAcceptListEmpty ? [
            files.every(file => file !== null) ||
            (editMode && files.every(file => file === null))
        ] : [])
    ], [hasDateField, isValidDate, title, link, type, files, isAcceptListEmpty, editMode]);

    // Remove falsy values from fields array
    const fields = useMemo(() => [
        hasDateField && 'Date',
        title !== null && 'Title',
        link !== null && 'Link',
        type !== null && 'Type',
        !isAcceptListEmpty && `File${files.length > 1 ? 's' : ''} ${editMode ? `(optional${files.length > 1 ? '—upload all if using' : ''})` : ''}`
    ].filter(Boolean), [hasDateField, title, link, type, files, isAcceptListEmpty, editMode]);
    
    useEffect(() => {
        setCanUpload(conditions.every(cond => cond));
    }, [conditions, setCanUpload]);

    return (
        <div style={{ display: 'flex', gap: '5%', marginTop: '10px' }}>
            {fields.map((field, i) => (
                <span key={i} style={{
                    color: conditions[i] ?
                        (theme === 'dark' ? 'lightgreen' : 'green') :
                        (theme === 'dark' ? 'lightcoral' : 'red'),
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                }}>
                    {conditions[i] ? <Check /> : <X />} {field}
                </span>
            ))}
        </div>
    );
}